import React, { useEffect, useRef, useState } from "react";
import { EnvelopeSimple, PaperPlaneTilt, Eye } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import InlineShareButton from "../InlineShareButton";
import { shareOrigin } from "../../lib/shareOrigin";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WeeklyEmailCard() {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [shareText, setShareText] = useState(null);
  const shareFetched = useRef(false);

  const fetchShareText = () => {
    if (shareFetched.current) return;
    shareFetched.current = true;
    api.get("/email/weekly-current-text")
      .then(({ data }) => { if (data?.text) setShareText(data.text); })
      .catch(() => {});
  };

  const load = () => api.get("/admin/email/weekly-status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (test) => {
    if (test && !testEmail.trim()) { toast.error("Enter a test email address"); return; }
    if (!test && !window.confirm(`Send the weekly update to all ${status?.recipients ?? "?"} members now?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/send-weekly", null, {
        params: test ? { test_email: testEmail.trim() } : {},
      });
      toast.success(`Sent ${data.sent}/${data.recipients} · failed ${data.failed}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-tac-surface/40 border border-tac-border rounded-sm p-5 space-y-3" data-testid="admin-weekly-email-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <EnvelopeSimple size={18} weight="fill" className="text-[#4CC9F0]" />
          <h2 className="font-display font-black uppercase tracking-wider text-sm">Weekly Member Email</h2>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API}/api/admin/email/weekly-preview`}
            target="_blank"
            rel="noreferrer"
            data-testid="weekly-email-preview-btn"
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-tac-bg/85 border-tac-border text-tac-text hover:border-tac-borderBright transition-colors flex items-center gap-1.5"
          >
            <Eye size={13} weight="bold" /> Preview
          </a>
          <InlineShareButton
            testid="weekly-email-share-btn"
            url={`${shareOrigin()}/api/email/weekly-current`}
            title="Firewatch Weekly Update"
            text="This week's fires, containment, evacuation levels and lightning — via Okanogan Highlands Firewatch"
            onOpen={fetchShareText}
            caption={
              shareText ||
              [
                "📬 FIREWATCH WEEKLY UPDATE",
                "This week's fires, containment %, evacuation levels, lightning and air quality across the region — the same update our members get by email.",
                "",
                `${shareOrigin()}/api/email/weekly-current`,
              ].join("\n")
            }
          />
        </div>
      </div>
      <div className="text-[12px] text-tac-dim">
        Fires, evac levels, lightning and air quality — auto-sends every <b className="text-tac-text">Monday 8 AM Pacific</b> to{" "}
        <b className="text-tac-text">{status?.recipients ?? "…"}</b> members ({status?.unsubscribed ?? 0} unsubscribed).
        {status?.last && (
          <> Last sent: <b className="text-tac-text">{new Date(status.last.at).toLocaleString()}</b> — {status.last.sent} delivered.</>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          data-testid="weekly-email-test-input"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="you@example.com"
          className="px-2 py-1.5 text-[12px] bg-tac-bg border border-tac-border rounded-sm text-tac-text w-52"
        />
        <button
          data-testid="weekly-email-send-test-btn"
          disabled={sending}
          onClick={() => send(true)}
          className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-tac-bg/85 border-tac-border text-tac-text hover:border-tac-borderBright transition-colors disabled:opacity-50"
        >
          Send Test
        </button>
        <button
          data-testid="weekly-email-send-all-btn"
          disabled={sending}
          onClick={() => send(false)}
          className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-[#4CC9F0] bg-[#4CC9F0]/15 text-[#4CC9F0] hover:bg-[#4CC9F0]/25 transition-colors flex items-center gap-1.5 disabled:opacity-50"
        >
          <PaperPlaneTilt size={13} weight="bold" /> {sending ? "Sending…" : "Send To All Members"}
        </button>
      </div>
    </div>
  );
}
